import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { ItemListResponse, SaveResponse } from 'helpers/interfaces';
import * as yup from 'yup';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { ticketContext } from 'views/Ticket/TicketContext';
import { appUserContext } from 'helpers/services';
import { UserType } from 'helpers/enums';

interface IContext {
    isLoading: boolean
    isSaving: boolean
    messages: IMessage[]

    isValidMessage: (html: string) => boolean
    saveMessage: (message: Message) => Promise<void>
    deleteMessage: (messageId: string) => Promise<void>
}

export interface IMessage {
    messageId: string
    canEdit: boolean
    canDelete: boolean
    text: string
    isPublic: boolean
    isFromCustomer: boolean
    createdBy: string
    createdByCompany: string
    createdOn: moment.Moment
    attachments: IAttachment[]
}

interface IAttachment {
    fileId: string
    fileName: string
    extension: string
    downloadUrl: string
    isImage: boolean
    createdOn: moment.Moment
    createdBy: string
    createdByCompany: string
}

export let attachmentSchema = yup.object({
    fileId: yup.string().default('').required("FileId not provided"),
    fileName: yup.string().default('').required("Filename not provided")
})

export let messageSchema = yup.object().shape({
    ticketId: yup.string().default('').required('Ticket not provided'),
    text: yup.string().default(''),
    isPublic: yup.boolean().default(false),
    attachments: yup.array(attachmentSchema).default([]),
    waitingForReply: yup.boolean().default(true),
})

type Message = yup.InferType<typeof messageSchema>;

export let messageContext = React.createContext({} as IContext);

let { Provider } = messageContext;

let MessageProvider: React.FC<{ children: React.ReactNode, ticketId: string }> = ({ children, ticketId }) => {
    let { enqueueSnackbar } = useSnackbar();

    let { currentUser } = useContext(appUserContext)
    let { ticket, getTicket, setStatusDialog } = useContext(ticketContext)

    let [isLoading, setLoading] = useState(true);
    let [messages, setMessages] = useState<IMessage[]>([]);
    let [isSaving, setSaving] = useState(false);


    useEffect(() => {
        getTicketMessages();
    }, [ticketId])


    let getTicketMessages = async () => {
        try {
            setLoading(true);
            let res = await axios.get<ItemListResponse<IMessage>>('/api/TicketMessage/GetTicketMessages', { params: { ticketId } })
            setMessages(res.data.items);
        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let saveMessage = async (message: Message) => {
        try {
            setSaving(true);

            let request = {
                ...message,
                sendNotification: true
            };

            let response = await axios.post<SaveResponse>('/api/TicketMessage/SaveTicketMessage', request);

            if (currentUser?.typology == UserType.VitecUser && !message.messageId && ticket.isReadOnly === false)
                setStatusDialog({ open: true, suggestedStatus: response.data.ticketStatusId })

            enqueueSnackbar('Message sent', { variant: 'success' });

            if (message.waitingForReply === true) {
                await getTicket();
            }

            await getTicketMessages();

        } catch (err) {
            setSaving(false);
        }

        setSaving(false);
    }

    let deleteMessage = async (messageId: string) => {
        if (window.confirm("Are you sure that you want to delete this message? Also attachments will be removed.") === false)
            return;

        try {
            await axios.delete(`/api/TicketMessage/DeleteTicketMessage/${messageId}`);
            await getTicketMessages();

        } catch (err) { }
    }

    let isValidMessage = (html: string) => {
        let tmp = document.createElement("DIV");
        tmp.innerHTML = html;
        return (tmp.textContent || tmp.innerText || "").trim() !== '';
    }


    return (
        <Provider
            value={{
                isLoading,
                messages,
                isSaving,

                isValidMessage,
                saveMessage,
                deleteMessage
            }}
        >
            {children}
        </Provider>
    )
}

export default MessageProvider;